import { Component, OnInit } from '@angular/core';
import { ToastModule } from 'primeng/toast';
import { CommonModule } from '@angular/common';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FileUploadModule } from 'primeng/fileupload';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { EncryptionService } from '../../../core/services/encryption';
import { FileService } from '../../../core/services/file';
import { MessageService, ConfirmationService } from 'primeng/api';
import { Router, ActivatedRoute } from '@angular/router';
import { ProgressBarModule } from 'primeng/progressbar';
import { InteractionService, IInteraction } from '../../../core/services/interaction.service';
import { ReservationService } from '../../../core/services/reservation.service';
import { CustomConfirmModalComponent } from '../../../shared/components/custom-confirm-modal/custom-confirm-modal.component';
import { LoadingSpinnerComponent } from '../../../shared/components/loading-spinner/loading-spinner.component';

@Component({
  selector: 'app-log-interaction',
  imports: [ToastModule, CommonModule, CardModule, InputTextModule, SelectModule, ButtonModule, ReactiveFormsModule, FileUploadModule, RadioButtonModule, ProgressBarModule, ConfirmDialogModule, CustomConfirmModalComponent, LoadingSpinnerComponent],
  templateUrl: './log-interaction.html',
  styleUrl: './log-interaction.scss',
  providers: [MessageService, ConfirmationService]
})
export class LogInteraction implements OnInit {
  sheduleInteractionForm: FormGroup;
  isFileUploading = false;
  uploadedFileUrl: string | null = null;
  uploadedFile: File | null = null;
  dragOver: boolean = false;
  isSubmitting = false;
  isMainButtonLoading = false;
  isJustSaveButtonLoading = false;
  isLoading: boolean = false;
  isEditMode: boolean = false;
  
  // Custom modal properties
  showUpcomingInteractionsModal: boolean = false;
  upcomingInteractionsMessage: string = '';
  pendingFormValue: any = null;
  pendingUpcomingInteractions: IInteraction[] = [];
  
  // URL parameters
  interactionId: string | null = null;
  reservationId: string | null = null;
  
  // Current interaction data (for edit mode)
  currentInteraction: IInteraction | null = null;
  
  // Properties to control field editability in edit mode
  get isInteractionTypeDisabled(): boolean {
    return this.isEditMode;
  }
  
  get isInteractionDateDisabled(): boolean {
    return this.isEditMode;
  }
  
  get isInteractionTimeDisabled(): boolean {
    return this.isEditMode;
  }
  
  interactionTypeOptions: any[] = [
    { label: 'Sales Call', value: 'sales_call' },
    { label: 'Demo', value: 'demo' },
    { label: 'Service Call', value: 'service_call' },
    { label: 'Follow-up Visit', value: 'follow_up' },
    { label: 'Emergency Call', value: 'emergency' }
  ];
  
  interactionStatusOptions: any[] = [
    { label: 'Completed', value: 'completed' },
    { label: 'Upcoming', value: 'upcoming' }
  ];

  reservationStatusOptions: any[] = [
    // { label: 'Expired', value: 'Expired' },
    { label: 'Converted', value: 'Converted' },
    { label: 'Not Interested', value: 'Not Interested' },
    // { label: 'Pending', value: 'Pending' },
    { label: 'Interested', value: 'Interested' }
  ];

  constructor(
    private fb: FormBuilder,
    private fileService: FileService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private router: Router,
    private route: ActivatedRoute,
    private encryption: EncryptionService,
    private interactionService: InteractionService,
    private reservationService: ReservationService
  ) {
    this.sheduleInteractionForm = this.fb.group({
      interactionType: ['', Validators.required],
      interactionDate: ['', Validators.required],
      interactionTime: ['', Validators.required],
      interactionStatus: ['upcoming', Validators.required],
      interactionNotes: [''],
      reservationStatus: [''],
      documents: ['']
    });
  }

  ngOnInit(): void {
    // Check route params synchronously first to reset form immediately
    const params = this.route.snapshot.params;
    if (params['reservationId'] && !params['interactionId']) {
      // We're in create mode - set reservationId and reset form immediately
      this.reservationId = params['reservationId'];
      this.isEditMode = false;
      this.resetForm();
    }
    
    this.getUrlParameters();
    this.setupFormValidation();
  }

  getUrlParameters(): void {
    this.route.params.subscribe(params => {
      if (params['interactionId']) {
        this.interactionId = params['interactionId'];
        this.isEditMode = true;
        this.loadInteractionData();
        console.log('Edit mode: Loading interaction with ID:', this.interactionId);
      } else if (params['reservationId']) {
        this.reservationId = params['reservationId'];
        this.isEditMode = false;
        this.resetForm();
        console.log('Create mode: Creating interaction for reservation:', this.reservationId);
      }
    });
  }

  private resetForm(): void {
    this.sheduleInteractionForm.reset({
      interactionType: '',
      interactionDate: '',
      interactionTime: '',
      interactionStatus: 'upcoming',
      interactionNotes: '',
      reservationStatus: '',
      documents: ''
    });
    
    // Enable all form controls in create mode
    this.sheduleInteractionForm.get('interactionType')?.enable();
    this.sheduleInteractionForm.get('interactionDate')?.enable();
    this.sheduleInteractionForm.get('interactionTime')?.enable();
    
    this.uploadedFileUrl = null;
    this.uploadedFile = null;
    this.isMainButtonLoading = false;
    this.isJustSaveButtonLoading = false;
    
    // Clear the file input
    const fileInput = document.getElementById('file-input-idCard') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  setupFormValidation(): void {
    // Listen to interaction status changes to update reservation status validation
    this.sheduleInteractionForm.get('interactionStatus')?.valueChanges.subscribe(status => {
      const reservationStatusControl = this.sheduleInteractionForm.get('reservationStatus');
      
      if (status === 'completed') {
        // Make reservation status required when interaction status is completed
        reservationStatusControl?.setValidators([Validators.required]);
        reservationStatusControl?.updateValueAndValidity();
      } else {
        // Remove required validation when interaction status is not completed
        reservationStatusControl?.clearValidators();
        reservationStatusControl?.updateValueAndValidity();
      }
    });

    // Set initial validation state based on current interaction status
    const currentStatus = this.sheduleInteractionForm.get('interactionStatus')?.value;
    if (currentStatus === 'completed') {
      const reservationStatusControl = this.sheduleInteractionForm.get('reservationStatus');
      reservationStatusControl?.setValidators([Validators.required]);
      reservationStatusControl?.updateValueAndValidity();
    }
  }

  // Getter to check if status is completed
  get isCompletedStatus(): boolean {
    return this.sheduleInteractionForm.get('interactionStatus')?.value === 'completed';
  }

  // Getter to check if reservation status is required
  get isReservationStatusRequired(): boolean {
    return this.isCompletedStatus;
  }

  get submitButtonText(): string {
    const reservationStatus = this.sheduleInteractionForm.get('reservationStatus')?.value;
    if (reservationStatus === 'Interested') {
      return 'Save and schedule follow-up';
    }
    return 'Save';
  }

  // Getter to check if we should show the second button (for Interested status)
  get shouldShowSecondButton(): boolean {
    const reservationStatus = this.sheduleInteractionForm.get('reservationStatus')?.value;
    return reservationStatus === 'Interested';
  }

  // Getter to get the second button text
  get secondButtonText(): string {
    return 'Just Save';
  }

  getReservationStatusDescription(): string {
    const selectedStatus = this.sheduleInteractionForm.get('reservationStatus')?.value;
    
    switch (selectedStatus) {
      case 'Converted':
        return 'Customer has successfully converted and will be redirected to create a contract.';
      case 'Not Interested':
        return 'Customer is not interested in the service. This will close the reservation.';
      case 'Interested':
        return 'Customer is interested. You can schedule more follow-up interactions later.';
      default:
        return '';
    }
  }

  loadInteractionData(): void {
    if (!this.interactionId) return;
    
    this.isLoading = true;
    this.interactionService.getInteractionById(this.interactionId).subscribe({
      next: (interaction) => {
        this.currentInteraction = interaction;
        this.populateForm(interaction);
        this.isLoading = false;
        console.log('Interaction data loaded:', interaction);
      },
      error: (error) => {
        console.error('Error loading interaction:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load interaction data'
        });
        this.isLoading = false;
      }
    });
  }

  populateForm(interaction: IInteraction): void {
    this.sheduleInteractionForm.patchValue({
      interactionType: interaction.interactionType,
      interactionDate: this.formatDateForInput(interaction.interactionDate),
      interactionTime: interaction.interactionTime,
      interactionStatus: 'completed', // Always set to completed in edit mode
      interactionNotes: interaction.interactionNotes || '',
      reservationStatus: interaction.reservationStatus || '',
      documents: interaction.documents?.[0] || ''
    });

    // Disable the form controls that should be read-only in edit mode
    if (this.isEditMode) {
      this.sheduleInteractionForm.get('interactionType')?.disable();
      this.sheduleInteractionForm.get('interactionDate')?.disable();
      this.sheduleInteractionForm.get('interactionTime')?.disable();
    }

    // Set reservationId from the interaction data for edit mode
    if (interaction.reservationId) {
      this.reservationId = typeof interaction.reservationId === 'string' 
        ? interaction.reservationId 
        : interaction.reservationId._id;
    }

    if (interaction.documents?.[0]) {
      this.uploadedFileUrl = interaction.documents[0];
    }

    // Since interaction status is always 'completed' in edit mode, make reservation status required
    const reservationStatusControl = this.sheduleInteractionForm.get('reservationStatus');
    reservationStatusControl?.setValidators([Validators.required]);
    reservationStatusControl?.updateValueAndValidity();
  }

  formatDateForInput(date: Date | string): string {
    if (!date) return '';
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  }

  formatDate(dateString: string | Date): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  }

  formatTime(timeString: string): string {
    if (!timeString) return 'N/A';
    
    // Convert 24-hour format to 12-hour format with AM/PM
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    
    return `${displayHour}:${minutes} ${ampm}`;
  }

  getInteractionTypeDisplay(interaction: IInteraction): string {
    if (!interaction.interactionType) return 'N/A';
    return interaction.interactionType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  onSubmit(): void {
    // Mark all fields as touched to show validation errors
    this.sheduleInteractionForm.markAllAsTouched();
    
    if (this.sheduleInteractionForm.valid) {
      const formValue = this.sheduleInteractionForm.value;
      
      // Check if reservation status is "Converted"
      if (formValue.reservationStatus === 'Converted') {
        this.showConvertedConfirmation(formValue);
      } else {
        this.processInteraction(formValue);
      }
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields.'
      });
    }
  }

  private showConvertedConfirmation(formValue: any): void {
    // First check if there are any upcoming interactions for this reservation
    this.checkUpcomingInteractionsBeforeConversion(formValue);
  }

  private checkUpcomingInteractionsBeforeConversion(formValue: any): void {
    if (!this.reservationId) {
      console.error('Reservation ID is not available');
      this.showConversionConfirmation(formValue);
      return;
    }

    this.interactionService.getInteractionsByReservationId(this.reservationId).subscribe({
      next: (interactions) => {
        const upcomingInteractions = interactions.filter(
          interaction => interaction.interactionStatus === 'upcoming' && interaction._id !== this.interactionId
        );

        if (upcomingInteractions.length > 0) {
          // Show popup with upcoming interactions and option to mark all as completed
          this.showUpcomingInteractionsPopup(formValue, upcomingInteractions);
        } else {
          // All interactions are completed, proceed with conversion confirmation
          this.showConversionConfirmation(formValue);
        }
      },
      error: (error) => {
        console.error('Error checking upcoming interactions:', error);
        // If error, proceed with normal flow
        this.showConversionConfirmation(formValue);
      }
    });
  }

  private showUpcomingInteractionsPopup(formValue: any, upcomingInteractions: IInteraction[]): void {
    const interactionList = upcomingInteractions.map(interaction => 
      `• ${this.getInteractionTypeDisplay(interaction)} on ${this.formatDate(interaction.interactionDate)} at ${this.formatTime(interaction.interactionTime)}`
    ).join('\n');

    this.upcomingInteractionsMessage = `You have ${upcomingInteractions.length} upcoming interaction(s) that need to be completed before converting to contract:\n\n${interactionList}\n\nWould you like to mark all interactions as completed and proceed to contract creation?`;
    this.pendingFormValue = formValue;
    this.pendingUpcomingInteractions = upcomingInteractions;
    this.showUpcomingInteractionsModal = true;
  }

  onConfirmUpcomingInteractions(): void {
    this.markAllInteractionsCompletedAndCreateContract(this.pendingFormValue, this.pendingUpcomingInteractions);
  }

  onCancelUpcomingInteractions(): void {
    this.showUpcomingInteractionsModal = false;
    this.pendingFormValue = null;
    this.pendingUpcomingInteractions = [];
  }

  private markAllInteractionsCompletedAndCreateContract(formValue: any, upcomingInteractions: IInteraction[]): void {
    if (!this.reservationId) {
      console.error('Reservation ID is not available');
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Reservation ID not found. Cannot mark interactions as completed.'
      });
      return;
    }

    this.isMainButtonLoading = true;
    
    this.interactionService.updateAllInteractionsToCompleted(this.reservationId).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `${response.modifiedCount || upcomingInteractions.length} interactions marked as completed!`
        });
        
        // Now process the current interaction and redirect to contract creation
        this.processInteraction(formValue, true);
      },
      error: (error) => {
        console.error('Error updating interactions:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to mark interactions as completed. Please try again.'
        });
        this.isMainButtonLoading = false;
      }
    });
  }

  private showConversionConfirmation(formValue: any): void {
    this.confirmationService.confirm({
      message: 'You have selected "Converted" status. This will redirect you to create a contract. Do you want to continue?',
      header: 'Create Contract',
      icon: 'pi pi-exclamation-triangle',
      rejectButtonStyleClass: 'p-button-outlined p-button-secondary',
      accept: () => {
        this.processInteraction(formValue, true);
      },
      reject: () => {
      }
    });
  }

  private processInteraction(formValue: any, redirectToContract: boolean = false, alwaysRedirectToView: boolean = false): void {
    this.isMainButtonLoading = true;
    
    const interactionData: Partial<IInteraction> = {
      interactionType: formValue.interactionType,
      interactionDate: formValue.interactionDate,
      interactionTime: formValue.interactionTime,
      interactionStatus: formValue.interactionStatus,
      interactionNotes: formValue.interactionNotes,
      reservationStatus: formValue.reservationStatus,
      documents: formValue.documents ? [formValue.documents] : []
    };

    if (this.isEditMode && this.interactionId) {
      // Update existing interaction
      this.updateInteraction(interactionData, redirectToContract, alwaysRedirectToView);
    } else if (this.reservationId) {
      // Create new interaction
      this.createInteraction(interactionData, redirectToContract, alwaysRedirectToView);
    }
  }

  createInteraction(interactionData: Partial<IInteraction>, redirectToContract: boolean = false, alwaysRedirectToView: boolean = false): void {
    if (!this.reservationId) return;

    const fullInteractionData: IInteraction = {
      ...interactionData as IInteraction,
      reservationId: this.reservationId
    };

    this.interactionService.createInteraction(fullInteractionData).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Interaction created successfully!'
        });
        this.isMainButtonLoading = false;
        
        if (redirectToContract) {
          // Redirect to contract creation page with reservationId
          this.router.navigate(['/admin/add-edit-contract', this.reservationId]);
        } else {
          // Always redirect to view reservation page
          this.router.navigate(['/admin/view-reservation', this.reservationId]);
        }
      },
      error: (error) => {
        console.error('Error creating interaction:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to create interaction. Please try again.'
        });
        this.isMainButtonLoading = false;
      }
    });
  }

  updateInteraction(interactionData: Partial<IInteraction>, redirectToContract: boolean = false, alwaysRedirectToView: boolean = false): void {
    if (!this.interactionId) return;

    this.interactionService.updateInteraction(this.interactionId, interactionData).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Interaction updated successfully!'
        });
        this.isMainButtonLoading = false;
        
        if (redirectToContract) {
          // Redirect to contract creation page with reservationId
          this.router.navigate(['/admin/add-edit-contract', this.reservationId]);
        } else {
          // Always redirect back to reservation view for updates (including "Just save")
          if (this.reservationId) {
            this.router.navigate(['/admin/view-reservation', this.reservationId]);
          } else {
            console.error('No reservationId available for redirect');
            this.router.navigate(['/admin/view-reservation']);
          }
        }
      },
      error: (error) => {
        console.error('Error updating interaction:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update interaction. Please try again.'
        });
        this.isMainButtonLoading = false;
      }
    });
  }

  onJustSave(): void {
    if (this.sheduleInteractionForm.valid && this.reservationId) {
      const formValue = this.sheduleInteractionForm.value;
      
      // Only override reservation status if interaction is not completed
      const modifiedFormValue = { 
        ...formValue, 
        reservationStatus: this.isCompletedStatus ? formValue.reservationStatus : 'ongoing' 
      };
      this.processJustSaveInteraction(modifiedFormValue, false, true); // Third parameter: alwaysRedirectToView = true
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields.'
      });
    }
  }

  processJustSaveInteraction(formValue: any, redirectToContract: boolean = false, alwaysRedirectToView: boolean = false): void {
    this.isJustSaveButtonLoading = true;
    
    const interactionData: Partial<IInteraction> = {
      interactionType: formValue.interactionType,
      interactionDate: formValue.interactionDate,
      interactionTime: formValue.interactionTime,
      interactionStatus: formValue.interactionStatus,
      interactionNotes: formValue.interactionNotes,
      reservationStatus: formValue.reservationStatus,
      documents: formValue.documents ? [formValue.documents] : []
    };

    if (this.isEditMode && this.interactionId) {
      // Update existing interaction
      this.updateInteractionForJustSave(interactionData, redirectToContract, alwaysRedirectToView);
    } else if (this.reservationId) {
      // Create new interaction
      this.createInteractionForJustSave(interactionData, redirectToContract, alwaysRedirectToView);
    }
  }

  createInteractionForJustSave(interactionData: Partial<IInteraction>, redirectToContract: boolean = false, alwaysRedirectToView: boolean = false): void {
    if (!this.reservationId) return;

    const fullInteractionData: IInteraction = {
      ...interactionData as IInteraction,
      reservationId: this.reservationId
    };

    this.interactionService.createInteraction(fullInteractionData).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Interaction created successfully!'
        });
        this.isJustSaveButtonLoading = false;
        
        if (redirectToContract) {
          // Redirect to contract creation page with reservationId
          this.router.navigate(['/admin/add-edit-contract', this.reservationId]);
        } else if (alwaysRedirectToView) {
          // Always redirect to view reservation page (for "Just save" button)
          this.router.navigate(['/admin/view-reservation', this.reservationId]);
        } else {
          // Check if reservation status is "Interested" to redirect to schedule interaction
          const reservationStatus = this.sheduleInteractionForm.get('reservationStatus')?.value;
          if (reservationStatus === 'Interested') {
            const targetUrl = `/admin/log-interaction/create/${this.reservationId}`;
            // If already on the same route, just reset the form
            if (this.router.url === targetUrl) {
              this.resetForm();
            } else {
              this.router.navigate(['/admin/log-interaction/create', this.reservationId], {
                onSameUrlNavigation: 'reload'
              });
            }
          } else {
            this.router.navigate(['/admin/view-reservation', this.reservationId]);
          }
        }
      },
      error: (error) => {
        console.error('Error creating interaction:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to create interaction. Please try again.'
        });
        this.isJustSaveButtonLoading = false;
      }
    });
  }

  updateInteractionForJustSave(interactionData: Partial<IInteraction>, redirectToContract: boolean = false, alwaysRedirectToView: boolean = false): void {
    if (!this.interactionId) return;

    this.interactionService.updateInteraction(this.interactionId, interactionData).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Interaction updated successfully!'
        });
        this.isJustSaveButtonLoading = false;
        
        if (redirectToContract) {
          // Redirect to contract creation page with reservationId
          this.router.navigate(['/admin/add-edit-contract', this.reservationId]);
        } else {
          // Always redirect back to reservation view for updates (including "Just save")
          if (this.reservationId) {
            this.router.navigate(['/admin/view-reservation', this.reservationId]);
          } else {
            console.error('No reservationId available for redirect');
            this.router.navigate(['/admin/view-reservation']);
          }
        }
      },
      error: (error) => {
        console.error('Error updating interaction:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update interaction. Please try again.'
        });
        this.isJustSaveButtonLoading = false;
      }
    });
  }

  onCancel(): void {
    this.router.navigate(['/admin/view-reservation', this.reservationId]);
  }


  private uploadImage(file: File): void {
    this.isFileUploading = true;

    // Store the file object for preview purposes
    this.uploadedFile = file;

    this.fileService.postFile(file, "USER_IMAGE_UPLOAD").subscribe({
      next: (res) => {
        console.log('LogInteraction: Upload successful', res);
        this.isFileUploading = false;
        const data = res as any;

        // Store the uploaded file URL
        this.uploadedFileUrl = data.url;

        // Update the form with the uploaded file URL
        this.sheduleInteractionForm.patchValue({
          documents: data.url
        });

        this.fileService.fileUploadingSubject.next({
          type: "USER_IMAGE_UPLOAD",
          loading: false,
        });

        this.messageService.add({
          severity: 'success',
          summary: 'File uploaded successfully',
          detail: `${file.name} has been uploaded`
        });
      },
      error: (error) => {
        console.error('LogInteraction: Upload error', error);
        this.isFileUploading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Upload failed',
          detail: error.error?.message || `Failed to upload ${file.name}`
        });
      }
    });
  }

  removeUploadedFile(): void {
    this.uploadedFileUrl = null;
    this.uploadedFile = null;

    // Clear the documents form control
    this.sheduleInteractionForm.patchValue({
      documents: ''
    });

    // Clear the file input
    const fileInput = document.getElementById('file-input-idCard') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }

    this.messageService.add({
      severity: 'info',
      summary: 'File removed',
      detail: 'File has been removed'
    });
  }

  // Method to check if a file is an image
  isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  // Drag and drop methods
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];
    
    // Use centralized validation
    const validation = this.fileService.validateFile(file, 'interaction');
    if (!validation.isValid) {
      this.messageService.add({
        severity: 'error',
        summary: 'File validation failed',
        detail: validation.errors.join(', ')
      });
      return;
    }

    // Upload the file
    this.uploadImage(file);
  }

  onFileUpload(event: any): void {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      this.uploadImage(file);
    }
  }
}
